import { Logo } from '@/components/Header';
import { ArrowLeft } from 'lucide-react';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-5 flex items-center justify-between">
          <Logo />
          <a href="/" className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Cookie Policy</h1>
        <p className="text-sm text-stone-400 mb-12">Last updated: January 1, 2026</p>

        <div className="prose prose-stone max-w-none space-y-8 text-stone-600 leading-relaxed text-[15px]">
          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">1. What Are Cookies</h2>
            <p>Cookies are small text files stored on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners. This Cookie Policy complies with the ePrivacy Directive 2002/58/EC and the GDPR.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">2. How We Use Cookies</h2>
            <p>SalesWall AI uses cookies for the following purposes:</p>

            <h3 className="text-base font-semibold text-stone-800 mt-4 mb-2">Strictly Necessary Cookies</h3>
            <p>These cookies are essential for the website to function properly. They enable basic functionalities like page navigation and access to secure areas. The website cannot function properly without these cookies. No consent is required for strictly necessary cookies under EU law.</p>

            <h3 className="text-base font-semibold text-stone-800 mt-4 mb-2">Analytics Cookies (Optional)</h3>
            <p>With your consent, we may use analytics cookies to understand how visitors interact with our website, helping us improve the user experience. These cookies collect information anonymously.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">3. Consent</h2>
            <p>In accordance with EU law, we will request your consent before placing any non-essential cookies on your device. You can give or withdraw your consent at any time. Strictly necessary cookies do not require consent.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">4. Cookies We Use</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-stone-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-stone-50">
                    <th className="text-left px-4 py-3 font-semibold text-stone-800 border-b border-stone-200">Cookie Name</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-800 border-b border-stone-200">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-800 border-b border-stone-200">Purpose</th>
                    <th className="text-left px-4 py-3 font-semibold text-stone-800 border-b border-stone-200">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100">
                    <td className="px-4 py-3">session_id</td>
                    <td className="px-4 py-3">Strictly Necessary</td>
                    <td className="px-4 py-3">Maintains your session state</td>
                    <td className="px-4 py-3">Session</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">cookie_consent</td>
                    <td className="px-4 py-3">Strictly Necessary</td>
                    <td className="px-4 py-3">Stores your cookie preferences</td>
                    <td className="px-4 py-3">1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">5. Third-Party Cookies</h2>
            <p>We do not currently use third-party cookies. If this changes in the future, we will update this policy and request your explicit consent before any third-party cookies are placed on your device.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">6. Managing Cookies</h2>
            <p>You can manage and delete cookies through your browser settings. Please note that disabling strictly necessary cookies may affect the functionality of our website. Here are links to cookie management instructions for common browsers:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">Safari</a></li>
              <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">Microsoft Edge</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">7. Your Rights</h2>
            <p>Under the GDPR and ePrivacy Directive, you have the right to:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>Be informed about what cookies we use and why.</li>
              <li>Give or withdraw consent for non-essential cookies at any time.</li>
              <li>Request deletion of data collected through cookies.</li>
              <li>Lodge a complaint with your local Data Protection Authority.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">8. Changes to This Policy</h2>
            <p>We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated revision date.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-3">9. Contact Us</h2>
            <p>If you have any questions about our use of cookies, please contact us at:</p>
            <p className="bg-stone-50 rounded-lg p-4 border border-stone-100">
              <a href="mailto:saleswall@saleswall.ai" className="text-violet-600 hover:text-violet-700 underline underline-offset-4">saleswall@saleswall.ai</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
